import React, { useMemo } from 'react';
import AutoSizeTitle from '../../components/autoSizeTitle/AutoSizeTitle';
import IndividualInformHeader from '../../components/individualInformHeader/individualInformHeader';
import RiskProfilesBarChartSelfcare from '../../graphics/selfcare/riskProfilesBarChartSelfcare';
import {
    getPrimarySelfcareProfiles,
    getResolvedSelfcareRiskProfiles,
    roundHalfUpInteger,
    getSecondarySelfcareProfiles,
} from './selfcareRiskProfiles';
import './pageRiskProfilesSelfcare.css';

const PageRiskProfilesSelfcare = ({ selectedEvaluation, individualResult, orgLogo, pageNumber }) => {
    const backgroundStyle = {
        backgroundImage: `url('/informs/pagina.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left top',
        backgroundSize: '100% auto',
    };

    const profiles = useMemo(() => getResolvedSelfcareRiskProfiles(individualResult), [individualResult]);
    const primaryProfiles = useMemo(() => getPrimarySelfcareProfiles(profiles), [profiles]);
    const secondaryProfiles = useMemo(() => getSecondarySelfcareProfiles(profiles), [profiles]);
    const primary = primaryProfiles[0] || profiles[0] || null;
    const secondary = secondaryProfiles[0] || profiles[1] || null;
    const textProfiles = [primary, secondary].filter(Boolean);

    const PRIMARY_OPERATIVE_READINGS = {
        Fatigado: 'Esta es tu vulnerabilidad más activa hoy. El cansancio físico reduce la capacidad de reacción y aumenta la probabilidad de omisiones en tareas que exigen atención sostenida. Es la prioridad de intervención inmediata.',
        Impulsivo: 'Esta es tu vulnerabilidad más activa hoy. Las decisiones tomadas bajo tensión emocional aumentan la probabilidad de errores en tareas de riesgo. Es la prioridad de intervención inmediata.',
        Distraído: 'Esta es tu vulnerabilidad más activa hoy. Operar en piloto automático eleva el riesgo de pasar por alto cambios en las condiciones del entorno. Es la prioridad de intervención inmediata.',
        Silencioso: 'Esta es tu vulnerabilidad más activa hoy. No reportar lo que observas mantiene riesgos ocultos y limita la capacidad del equipo para protegerse. Es la prioridad de intervención inmediata.',
        Indisciplinado: 'Esta es tu vulnerabilidad más activa hoy. Omitir pasos del procedimiento, especialmente bajo presión, es el mecanismo más frecuente asociado a accidentes. Es la prioridad de intervención inmediata.',
        Aislado: 'Esta es tu vulnerabilidad más activa hoy. No verificar al compañero ni pedir ayuda deja al equipo sin barreras de protección colectiva. Es la prioridad de intervención inmediata.',
        Desconectado: 'Esta es tu vulnerabilidad más activa hoy. Cuando la motivación para cuidarse baja, los hábitos seguros se erosionan gradualmente sin señales visibles. Es la prioridad de intervención inmediata.',
    };

    const getSecondaryOperativeReading = (secondaryProfile, primaryProfile) => {
        const secondaryName = secondaryProfile?.title || '';
        const primaryName = primaryProfile?.title || '';
        const tierKey = secondaryProfile?.tierKey || '';

        if (tierKey === 'critical' || tierKey === 'high') {
            return `Acompaña al perfil principal (${primaryName}). La combinación de ${secondaryName} con ${primaryName} eleva significativamente la exposición porque ambas vulnerabilidades operan simultáneamente, reduciendo las barreras de protección disponibles.`;
        }
        if (tierKey === 'moderate') {
            return `Acompaña al perfil principal (${primaryName}). Esta dimensión es la siguiente a monitorear preventivamente. Hoy se mantiene en rango aceptable, pero puede activarse si las condiciones del entorno o la carga aumentan.`;
        }
        // low / protector
        return `Acompaña al perfil principal (${primaryName}). Esta es una dimensión protectora actualmente. Mantener las prácticas de cuidado asociadas a ${secondaryName} para sostener este nivel.`;
    };

    const renderProfileCard = (profile, index) => {
        const isPrimary = index === 0;
        const operativeText = isPrimary
            ? (PRIMARY_OPERATIVE_READINGS[profile.title] || 'Este perfil representa la dimensión con mayor vulnerabilidad relativa. Es la prioridad de intervención.')
            : getSecondaryOperativeReading(profile, primary);

        return (
            <div key={profile.key} className="selfcare-profile-card">
                <div className="selfcare-profile-card-badge">
                    {isPrimary ? 'PERFIL PRINCIPAL' : 'PERFIL SECUNDARIO'}
                </div>
                <div className="selfcare-profile-card-title">{profile.title}</div>
                <div className="selfcare-profile-card-score">
                    Nivel de perfil: {roundHalfUpInteger(profile.level)} / 100 - {profile.tierLabel}
                </div>

                <div className="selfcare-profile-card-section-title">Factor asociado</div>
                <div className="selfcare-profile-card-body">{profile.factor}</div>

                <div className="selfcare-profile-card-section-title">Lectura del resultado</div>
                <div className="selfcare-profile-card-body">{profile.description}</div>

                <div className="selfcare-profile-card-section-title">Lectura operativa</div>
                <div className="selfcare-profile-card-body">
                    {operativeText}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="pdf-page internal-page">
                <div className="internal-page-content" style={backgroundStyle}>
                    <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                    <div className="internal-page-info internal-page-info-individual">
                        <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                        <div className="selfcare-profiles-intro">
                            El informe revisa siete perfiles de riesgo derivados de las dimensiones del IASH. Cada perfil expresa el nivel de vulnerabilidad observado hoy en esa dimensión, calculado como el complemento del puntaje protector. El perfil principal corresponde al mayor nivel de vulnerabilidad y el secundario al siguiente nivel más alto.
                        </div>

                        <div className="selfcare-profiles-summary-card">
                            <div className="selfcare-profiles-summary-label">Perfil principal</div>
                            <div className="selfcare-profiles-summary-value">
                                {primary ? String(primary.title).toUpperCase() : 'SIN PERFIL'}
                            </div>
                            <div className="selfcare-profiles-summary-score">
                                {primary ? `${roundHalfUpInteger(primary.level)} / 100 - ${primary.tierLabel}` : 'Sin datos suficientes'}
                            </div>
                        </div>

                        <div className="selfcare-profiles-box">
                            <div className="selfcare-profiles-title">Perfil de riesgo</div>

                            <div className="selfcare-profiles-chart selfcare-profiles-chart--full">
                                <RiskProfilesBarChartSelfcare profiles={profiles} />
                                <div className="selfcare-profiles-chart-note">
                                    Nivel de perfil = 100 - puntaje protector de la dimensión.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="internal-page-number">{pageNumber}</div>
                </div>
            </div>

            <div className="pdf-page internal-page">
                <div className="internal-page-content" style={backgroundStyle}>
                    <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                    <div className="internal-page-info internal-page-info-individual">
                        <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                        <div className="selfcare-profiles-box selfcare-profiles-box--textual">
                            <div className="selfcare-profiles-title">Lectura de perfiles</div>

                            {textProfiles.length > 0 ? (
                                <div className="selfcare-profiles-cards selfcare-profiles-cards--page">
                                    {textProfiles.map((profile, index) => renderProfileCard(profile, index))}
                                </div>
                            ) : (
                                <div className="selfcare-profiles-safe-box">
                                    <div className="selfcare-profiles-safe-title">Sin datos suficientes</div>
                                    <div className="selfcare-profiles-safe-body">
                                        No fue posible construir los perfiles porque faltan resultados por dimensión.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="internal-page-number">{pageNumber + 1}</div>
                </div>
            </div>
        </>
    );
};

export default PageRiskProfilesSelfcare;
